/**
 * TaskReference — a custom inline TipTap node for embedded task references.
 *
 * Renders as a small inline chip:  [✓] Task title
 * The checkbox is rendered via a React NodeView (TaskReferenceView)
 * making it clickable for toggling task status.
 *
 * Attributes stored in the document:
 *   - id:      the task's database ID
 *   - title:   the task title text
 *   - checked: whether the task is complete
 */

import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { TaskReferenceView } from './TaskReferenceView';

export interface TaskReferenceAttrs {
  id: string | null;
  title: string | null;
  checked: boolean;
}

export interface TaskReferenceOptions {
  onTaskStatusChange?: (taskId: string, newStatus: string) => void;
}

export const TaskReference = Node.create<TaskReferenceOptions>({
  name: 'taskReference',

  group: 'inline',
  inline: true,
  selectable: true,
  atom: true,

  addOptions() {
    return {
      onTaskStatusChange: undefined,
    };
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-task-id'),
        renderHTML: (attributes) => {
          if (!attributes.id) return {};
          return { 'data-task-id': attributes.id };
        },
      },
      title: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-task-title'),
        renderHTML: (attributes) => {
          if (!attributes.title) return {};
          return { 'data-task-title': attributes.title };
        },
      },
      checked: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-checked') === 'true',
        renderHTML: (attributes) => {
          return { 'data-checked': String(!!attributes.checked) };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="taskReference"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(
        {
          'data-type': 'taskReference',
          class: 'task-reference-node',
        },
        HTMLAttributes,
      ),
      HTMLAttributes['data-task-title'] || 'Untitled task',
    ];
  },

  renderText({ node }) {
    const checked = node.attrs.checked ? '[x]' : '[ ]';
    return `${checked} ${node.attrs.title || 'Untitled task'}`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(TaskReferenceView, {
      as: 'span',
      className: 'task-reference-nodeview-wrapper',
    });
  },
});
